/**
 * Audit Logs Page (Admin Only)
 *
 * Security Design:
 * - Displays the append-only audit trail
 * - Admin can filter by event type and date range
 * - Admin CANNOT modify or delete any log entries
 * - Viewing audit logs is itself recorded in the audit trail
 * - Export to PDF/Excel for forensic records
 *
 * Event types:
 * LOGIN, UPLOAD, DECRYPT_REQUEST, OTP_GENERATED,
 * OTP_VERIFIED, OTP_FAILED, ADMIN_ACCESS, SECURITY_ALERT
 */

import React, { useState, useEffect, useMemo } from 'react';
import { getAuditLogs, exportAuditLogsCsv } from '../services/api';
import { formatDateTime, formatRelative as utilFormatRelative, getTimezoneLabel, parseUTC } from '../utils/time';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const ITEMS_PER_PAGE = 15;

/** UI-only subtitle for audit insight header (derived from event_type). */
function getEventCategorySubtitle(eventType) {
  if (!eventType) return 'System event';
  if (['LOGIN', 'LOGIN_SUCCESS', 'LOGIN_FAIL', 'LOGOUT', 'REGISTER'].includes(eventType)) return 'Authentication event';
  if (eventType.startsWith('OTP_')) return 'One-time password event';
  if (eventType.startsWith('ACCESS_')) return 'File access event';
  if (eventType.startsWith('ADMIN_')) return 'Administration event';
  if (eventType.includes('SECURITY_ALERT')) return 'Security event';
  if (eventType.startsWith('USER_')) return 'User lifecycle event';
  if (eventType === 'DELETE_USER' || eventType.startsWith('ADMIN_USER_')) return 'User lifecycle event';
  if (eventType.startsWith('FILE_DELETE') || eventType === 'FILE_DELETED') return 'File deletion event';
  if (['UPLOAD', 'FILE_UPLOAD', 'ENCRYPTED_DOWNLOAD', 'FILE_DOWNLOAD', 'CLIENT_DECRYPT_COMPLETE'].includes(eventType)) return 'File transfer event';
  if (eventType === 'AUDIT_EXPORT') return 'Compliance export event';
  return 'System event';
}

/** Short browser + OS label for display; full string shown when expanded. */
function parseUserAgentShort(userAgent) {
  if (!userAgent || typeof userAgent !== 'string') return { label: '—', full: '' };
  const u = userAgent;
  let browser = 'Unknown browser';
  if (/Edg\//.test(u)) browser = 'Edge';
  else if (/Chrome\//.test(u)) browser = 'Chrome';
  else if (/Firefox\//.test(u)) browser = 'Firefox';
  else if (/Safari\//.test(u) && !/Chrome\//.test(u)) browser = 'Safari';
  else if (/MSIE|Trident/.test(u)) browser = 'Internet Explorer';

  let os = '';
  if (/Windows NT 10\./.test(u)) os = 'Windows 10/11';
  else if (/Windows NT 6\.3/.test(u)) os = 'Windows 8.1';
  else if (/Windows NT 6\.2/.test(u)) os = 'Windows 8';
  else if (/Windows NT 6\.1/.test(u)) os = 'Windows 7';
  else if (/Windows/.test(u)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(u)) os = 'macOS';
  else if (/iPhone|iPad|iPod/.test(u)) os = 'iOS';
  else if (/Android/.test(u)) os = 'Android';
  else if (/Linux/.test(u)) os = 'Linux';

  const label = os ? `${browser} (${os})` : browser;
  return { label, full: userAgent };
}

function outcomePillVariant(outcome) {
  if (outcome === 'SUCCESS') return 'success';
  if (outcome === 'FAILED') return 'failed';
  if (outcome === 'FLAGGED') return 'flagged';
  return 'neutral';
}

/** Table row: outcome tier for tri-state event pill (Success / Failed / Info). */
function rowOutcomeTier(outcome) {
  if (outcome === 'SUCCESS') return 'success';
  if (outcome === 'FAILED') return 'failed';
  return 'info';
}

function leftRowIndicatorColor(outcome) {
  if (outcome === 'SUCCESS') return 'rgba(34, 197, 94, 0.65)';
  if (outcome === 'FAILED') return 'rgba(239, 68, 68, 0.6)';
  return 'rgba(99, 102, 241, 0.38)';
}

function outcomeCellColor(outcome) {
  if (outcome === 'SUCCESS') return 'var(--green)';
  if (outcome === 'FAILED') return 'var(--red)';
  return 'var(--text-muted)';
}

const iconAuditTotal = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
);
const iconAuditSuccess = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
);
const iconAuditFailed = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
);
const iconAuditShield = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
);

function AuditLogsPage() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [searchText, setSearchText] = useState(''); // client-side text search
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // IMPORTANT: Default sort is by server-side timestamp, descending (latest first).
  // This ensures the audit timeline is logically ordered regardless of log ID.
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [uaDetailExpanded, setUaDetailExpanded] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Resolve selected log by ID only. Never use index or array position.
  const selectedLog = selectedLogId ? logs.find((l) => l.id === selectedLogId) ?? null : null;

  useEffect(() => {
    setUaDetailExpanded(false);
  }, [selectedLogId]);

  const eventTypes = [
    '', 'LOGIN', 'LOGIN_SUCCESS', 'LOGIN_FAIL', 'LOGOUT', 'REGISTER',
    'UPLOAD', 'FILE_UPLOAD', 'ENCRYPTED_DOWNLOAD', 'FILE_DOWNLOAD', 'CLIENT_DECRYPT_COMPLETE',
    'OTP_GENERATED', 'OTP_VERIFIED', 'OTP_SUCCESS', 'OTP_FAILED', 'OTP_FAIL', 'OTP_RATE_LIMITED',
    'ACCESS_REQUEST', 'ACCESS_APPROVED', 'ACCESS_REJECTED', 'ACCESS_REVOKED', 'ACCESS_DOWNLOAD',
    'USER_SUSPENDED', 'USER_ACTIVATED',
    'FILE_DELETE_SCHEDULED', 'FILE_DELETE_CANCELED', 'FILE_DELETE_EXECUTED', 'FILE_DELETED', 'FILE_DELETE',
    'SECURITY_ALERT_CREATED', 'SECURITY_ALERT_REVIEWED', 'SECURITY_ALERT_RESOLVED',
    'ADMIN_ACCESS', 'ADMIN_SCHEDULE_DELETE', 'ADMIN_CANCEL_DELETE', 'ADMIN_FORCE_DELETE',
    'ADMIN_ACCESS_APPROVE', 'ADMIN_ACCESS_REVOKE', 'ADMIN_ACCESS_EXTEND',
    'ADMIN_USER_SUSPEND', 'ADMIN_USER_ACTIVATE', 'ADMIN_USER_ROLE', 'DELETE_USER',
    'AUDIT_EXPORT',
  ];

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await getAuditLogs(
        filterType || null,
        null,
        null,
        startDate || null,
        endDate || null
      );
      // Use real data if available, enrich with display-friendly fields
      if (data && data.length > 0) {
        const enriched = data.map((log) => {
          // Parse the details JSON which contains resource, outcome, user_agent
          let parsed = {};
          if (log.details) {
            try { parsed = JSON.parse(log.details); } catch { parsed = {}; }
          }
          return {
            ...log,
            user_email: log.user_email || log.user_id || '—',
            resource: parsed.resource || extractResource(log.description, log.event_type),
            outcome: parsed.outcome || inferOutcome(log.description),
            user_agent: parsed.user_agent || null,
            location: inferLocation(log.ip_address),
            duration: null,
            session_id: null,
          };
        });
        setLogs(enriched);
      } else {
        // API returned empty — show empty state (no mock data)
        setLogs([]);
      }
    } catch (err) {
      console.error('[AuditLogs] Failed to load:', err);
      setLogs([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
      setCurrentPage(1);
    }
  };

  /** Extract resource name from description if not provided by backend details */
  const extractResource = (desc, eventType) => {
    if (!desc) return '—';
    // For login/register events, the resource is the auth endpoint
    if (eventType === 'LOGIN' || eventType === 'LOGIN_SUCCESS' || eventType === 'LOGIN_FAIL') {
      if (desc.includes('registered')) return '/auth/register';
      return '/auth/login';
    }
    if (eventType === 'ADMIN_ACCESS') {
      if (desc.includes('dashboard')) return '/admin/dashboard';
      if (desc.includes('audit')) return '/admin/audit-logs';
      if (desc.includes('user')) return '/admin/users';
      if (desc.includes('alert')) return '/admin/security-alerts';
      return '/admin';
    }
    // Try to extract filename (avoid matching emails)
    const fileMatch = desc.match(/:\s*([^\s(]+\.\w{2,5})(?:\s|$|\()/);
    if (fileMatch && !fileMatch[1].includes('@')) return fileMatch[1];
    // Try to extract API path
    const pathMatch = desc.match(/(\/\w+\/[\w-]+)/);
    if (pathMatch) return pathMatch[1];
    return '—';
  };

  /** Infer outcome from description text */
  const inferOutcome = (desc) => {
    if (!desc) return '—';
    const lower = desc.toLowerCase();
    if (lower.includes('failed') || lower.includes('invalid') || lower.includes('denied')) return 'FAILED';
    if (lower.includes('flagged') || lower.includes('suspected') || lower.includes('alert')) return 'FLAGGED';
    return 'SUCCESS';
  };

  /** Provide a location label based on IP address */
  const inferLocation = (ip) => {
    if (!ip) return null;
    if (ip === '127.0.0.1' || ip === '::1' || ip === 'localhost') return 'Local (Development)';
    if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.')) return 'Private Network';
    return null; // Unknown — GeoIP not available in dev
  };

  const handleFilter = (e) => {
    e.preventDefault();
    loadLogs();
  };

  const clearFilters = () => {
    setFilterType('');
    setSearchText('');
    setStartDate('');
    setEndDate('');
  };

  // Use shared timezone-aware utilities from utils/time.js
  const formatDate = (dateStr) => formatDateTime(dateStr);
  const formatRelative = (dateStr) => utilFormatRelative(dateStr);
  const tzLabel = getTimezoneLabel();

  // Filtering — copy then filter; never mutate logs
  const filteredLogs = useMemo(() => {
    let result = [...logs];
    if (filterType) result = result.filter((l) => l.event_type === filterType);
    if (startDate) result = result.filter((l) => l.created_at >= startDate);
    if (endDate) result = result.filter((l) => l.created_at <= endDate + 'T23:59:59');
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      result = result.filter((l) =>
        (l.description || '').toLowerCase().includes(q) ||
        (l.user_email || '').toLowerCase().includes(q) ||
        (l.resource || '').toLowerCase().includes(q) ||
        (l.event_type || '').toLowerCase().includes(q) ||
        (l.ip_address || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [logs, filterType, startDate, endDate, searchText]);

  // Sorting — copy then sort; never mutate logs or filteredLogs
  const sortedLogs = useMemo(() => {
    const sorted = [...filteredLogs];
    return sorted.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'created_at') {
        aVal = (parseUTC(aVal) || new Date(0)).getTime();
        bVal = (parseUTC(bVal) || new Date(0)).getTime();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredLogs, sortField, sortDir]);

  // Pagination (page size from Admin Settings: window.__cdeaas_itemsPerPage, set in App.js)
  const pageSizeUsed = typeof window !== 'undefined' && window.__cdeaas_itemsPerPage != null ? Number(window.__cdeaas_itemsPerPage) : ITEMS_PER_PAGE;
  const effectivePageSize = Number.isFinite(pageSizeUsed) && pageSizeUsed > 0 ? pageSizeUsed : ITEMS_PER_PAGE;
  const totalPages = Math.ceil(sortedLogs.length / effectivePageSize);
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const maxVisiblePageButtons = isMobile ? 3 : 5;
  const paginatedLogs = sortedLogs.slice(
    (safePage - 1) * effectivePageSize,
    safePage * effectivePageSize
  );

  const handleView = (id) => {
    console.log('AuditLogsPage view:', id);
    setSelectedLogId(id);
  };

  const summaryStats = useMemo(() => {
    let success = 0;
    let failed = 0;
    let securityAlerts = 0;
    filteredLogs.forEach((l) => {
      if (l.outcome === 'SUCCESS') success += 1;
      if (l.outcome === 'FAILED') failed += 1;
      if (l.event_type && l.event_type.includes('SECURITY_ALERT')) securityAlerts += 1;
    });
    return {
      total: filteredLogs.length,
      success,
      failed,
      securityAlerts,
    };
  }, [filteredLogs]);

  const hasActiveFilters = filterType || startDate || endDate || searchText;

  // ── Export functions ──────────────────────────────────────────
  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 12;
    const now = new Date().toLocaleString('en-IE');

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, 8, pageW - margin * 2, 22, 2, 2, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.2);
    doc.roundedRect(margin, 8, pageW - margin * 2, 22, 2, 2, 'S');

    doc.setFontSize(15);
    doc.setTextColor(15, 23, 42);
    doc.text('JOSMY — Audit Log Report', margin + 4, 17);
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${now}`, margin + 4, 23);
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total entries: ${filteredLogs.length}`, pageW - margin - 4, 19, { align: 'right' });
    doc.setFont('helvetica', 'normal');

    let tableStartY = 34;
    if (hasActiveFilters) {
      const filters = [];
      if (filterType) filters.push(`Event: ${filterType}`);
      if (startDate) filters.push(`From: ${startDate}`);
      if (endDate) filters.push(`To: ${endDate}`);
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(`Filters: ${filters.join('  ·  ')}`, margin + 4, 28, { maxWidth: pageW - margin * 2 - 8 });
      tableStartY = 36;
    }

    const headers = [['#', 'Timestamp', 'Event Type', 'Description', 'Resource', 'User', 'IP Address', 'Location', 'Outcome']];
    const rows = filteredLogs.map((log, i) => [
      String(i + 1),
      formatDate(log.created_at),
      log.event_type || '',
      String(log.description || '').replace(/\s+/g, ' ').trim(),
      log.resource || '—',
      String(log.user_email || '').replace(/\s+/g, ' ').trim(),
      log.ip_address || '—',
      log.location || '—',
      log.outcome || '—',
    ]);

    const innerW = pageW - margin * 2;

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: tableStartY,
      margin: { left: margin, right: margin, bottom: 14 },
      tableWidth: innerW,
      styles: {
        fontSize: 7.5,
        cellPadding: 2.2,
        valign: 'top',
        overflow: 'linebreak',
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [15, 23, 42],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: { fillColor: [252, 252, 254] },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 34 },
        2: { cellWidth: 24 },
        3: { cellWidth: 62 },
        4: { cellWidth: 26 },
        5: { cellWidth: 38 },
        6: { cellWidth: 22 },
        7: { cellWidth: 26 },
        8: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 8) {
          const v = data.cell.raw;
          if (v === 'SUCCESS') data.cell.styles.textColor = [22, 101, 52];
          else if (v === 'FAILED') data.cell.styles.textColor = [185, 28, 28];
          else if (v === 'FLAGGED') data.cell.styles.textColor = [161, 98, 7];
          else data.cell.styles.textColor = [100, 116, 139];
        }
      },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `JOSMY Audit Log · Page ${i} of ${pageCount} · CONFIDENTIAL`,
        pageW / 2,
        doc.internal.pageSize.getHeight() - 8,
        { align: 'center' }
      );
    }

    doc.save(`JOSMY_Audit_Log_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = () => {
    const rows = filteredLogs.map((log, i) => ({
      '#': i + 1,
      'Timestamp': formatDate(log.created_at),
      'Event Type': log.event_type,
      'Description': log.description,
      'Resource': log.resource,
      'User Email': log.user_email,
      'User ID': log.user_id,
      'IP Address': log.ip_address,
      'Location': log.location || '—',
      'Outcome': log.outcome,
      'Duration (ms)': log.duration,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    ws['!cols'] = [
      { wch: 6 }, { wch: 22 }, { wch: 22 }, { wch: 48 },
      { wch: 26 }, { wch: 28 }, { wch: 36 }, { wch: 14 },
      { wch: 22 }, { wch: 12 }, { wch: 10 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Audit Logs');
    XLSX.writeFile(wb, `JOSMY_Audit_Log_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async () => {
    try {
      const blob = await exportAuditLogsCsv(
        filterType || null,
        null,
        null,
        startDate || null,
        endDate || null
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[AuditLogs] CSV export failed:', err);
    }
  };

  return (
    <div className="page-container admin-page-shell admin-tactile-page admin-dashboard-premium">
      <style>{`
        .audit-log-main-panel {
          padding: 26px 28px 28px;
          margin-bottom: 24px;
        }
        @media (max-width: 768px) {
          .audit-stats-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 520px) {
          .audit-stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
        .audit-filter-row {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-end;
          gap: 18px;
          margin-bottom: 22px;
        }
        .audit-filter-date-wrap {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          align-items: flex-end;
        }
        .audit-filter-actions {
          margin-left: auto;
        }
        @media (min-width: 1100px) {
          .audit-filter-row {
            flex-wrap: nowrap;
          }
        }
        @media (max-width: 768px) {
          .audit-log-main-panel {
            padding: 18px 14px 16px;
          }
          .audit-filter-row {
            gap: 12px;
          }
          .audit-filter-row > * {
            width: 100%;
            min-width: 0 !important;
          }
          .audit-filter-date-wrap {
            width: 100%;
            gap: 10px;
          }
          .audit-filter-date-wrap > * {
            flex: 1 1 140px;
            min-width: 0 !important;
          }
          .audit-filter-actions {
            width: 100%;
            margin-left: 0;
            display: grid !important;
            grid-template-columns: 1fr 1fr;
            gap: 10px !important;
          }
          .audit-filter-actions button {
            width: 100%;
            justify-content: center;
            min-height: 42px;
          }
        }
        .audit-filter-input:focus-visible {
          outline: none;
          border-color: rgba(99, 102, 241, 0.4) !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
        }
        .audit-log-row {
          transition: background-color 0.18s ease;
        }
        .audit-log-row:hover {
          background-color: rgba(99, 102, 241, 0.06);
        }
        [data-theme="dark"] .audit-log-row:hover {
          background-color: rgba(99, 102, 241, 0.1);
        }
        .audit-event-pill {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 4px 9px;
          border-radius: 999px;
          white-space: nowrap;
          margin-bottom: 6px;
        }
        .audit-event-pill--success {
          color: #15803d;
          background: rgba(34, 197, 94, 0.12);
          border: 1px solid rgba(34, 197, 94, 0.28);
        }
        .audit-event-pill--failed {
          color: #b91c1c;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
        }
        .audit-event-pill--info {
          color: var(--text-muted);
          background: var(--surface-85);
          border: 1px solid var(--border);
        }
        [data-theme="dark"] .audit-event-pill--success {
          color: #4ade80;
          background: rgba(34, 197, 94, 0.1);
          border-color: rgba(34, 197, 94, 0.28);
        }
        [data-theme="dark"] .audit-event-pill--failed {
          color: #fca5a5;
          background: rgba(239, 68, 68, 0.08);
          border-color: rgba(239, 68, 68, 0.28);
        }
        .audit-page-btn {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border);
          border-radius: 8px;
          background-color: var(--surface-85);
          color: var(--text-body);
          font-size: 11px;
          cursor: pointer;
          font-weight: 600;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
        }
        .audit-page-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(99, 102, 241, 0.12);
          border-color: rgba(99, 102, 241, 0.2);
        }
        .audit-page-btn:disabled {
          opacity: 0.42;
          cursor: not-allowed;
        }
        .audit-page-btn--active {
          background: linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4);
          color: #fff;
          border-color: transparent;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.28);
        }
        .audit-mobile-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 14px;
        }
        .audit-mobile-card {
          border: 1px solid var(--border);
          border-left-width: 3px;
          border-radius: 12px;
          padding: 12px;
          background: var(--surface-90);
          cursor: pointer;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .audit-mobile-card:hover {
          border-color: rgba(99, 102, 241, 0.3);
          box-shadow: 0 4px 14px rgba(99, 102, 241, 0.12);
          background: var(--surface-95);
        }
        .audit-mobile-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
        }
        .audit-mobile-title {
          margin-top: 4px;
          font-size: 12px;
          font-weight: 700;
          color: var(--text-secondary);
          letter-spacing: 0.01em;
        }
        .audit-mobile-desc {
          margin-top: 6px;
          font-size: 13px;
          color: var(--text-body);
          line-height: 1.45;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .audit-mobile-meta {
          margin-top: 8px;
          display: grid;
          grid-template-columns: 1fr;
          gap: 4px;
          font-size: 11px;
          color: var(--text-muted);
        }
        .audit-mobile-meta code {
          font-size: 11px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          color: var(--text-secondary);
        }

        @keyframes auditInsightOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes auditInsightSheetIn {
          from { opacity: 0; transform: translateY(14px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .audit-insight-overlay {
          animation: auditInsightOverlayIn 0.22s ease-out forwards;
        }
        .audit-insight-sheet {
          animation: auditInsightSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .audit-insight-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          gap: 12px;
        }
        .audit-insight-eyebrow {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-muted);
        }
        .audit-insight-close {
          flex-shrink: 0;
          background: var(--surface-85);
          border: 1px solid var(--border);
          border-radius: 10px;
          cursor: pointer;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px;
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .audit-insight-close:hover {
          background: var(--surface-75);
          color: var(--text-primary);
          border-color: rgba(99, 102, 241, 0.25);
        }

        .audit-insight-hero {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 22px;
          padding-bottom: 20px;
          border-bottom: 1px solid var(--border);
        }
        .audit-insight-title {
          margin: 0 0 6px 0;
          font-size: 1.5rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          line-height: 1.15;
          background: linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .audit-insight-subtitle {
          margin: 0;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .audit-insight-outcome-pill {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          padding: 8px 14px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .audit-insight-outcome-pill--success {
          color: #15803d;
          background: rgba(34, 197, 94, 0.14);
          border: 1px solid rgba(34, 197, 94, 0.35);
          box-shadow: 0 0 18px rgba(34, 197, 94, 0.22);
        }
        .audit-insight-outcome-pill--failed {
          color: #b91c1c;
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.4);
          box-shadow: 0 0 18px rgba(239, 68, 68, 0.22);
        }
        .audit-insight-outcome-pill--flagged {
          color: #a16207;
          background: rgba(234, 179, 8, 0.14);
          border: 1px solid rgba(234, 179, 8, 0.35);
          box-shadow: 0 0 14px rgba(234, 179, 8, 0.18);
        }
        .audit-insight-outcome-pill--neutral {
          color: var(--text-muted);
          background: var(--surface-85);
          border: 1px solid var(--border);
          box-shadow: none;
        }

        .audit-insight-body {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .audit-insight-panel {
          background: var(--surface-90);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 16px 18px;
          box-shadow: 0 4px 20px rgba(15, 16, 40, 0.06);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .audit-insight-panel:hover {
          border-color: rgba(99, 102, 241, 0.14);
          box-shadow: 0 6px 24px rgba(99, 102, 241, 0.08);
        }
        .audit-insight-section-title {
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--text-muted);
          margin: 0 0 12px 0;
        }
        .audit-insight-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px 20px;
        }
        @media (max-width: 520px) {
          .audit-insight-grid-2 {
            grid-template-columns: 1fr;
          }
        }

        .audit-insight-field-label {
          display: block;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--text-muted);
          margin-bottom: 4px;
        }
        .audit-insight-field-value {
          display: block;
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.45;
          word-break: break-word;
        }
        .audit-insight-field-value--mono {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          font-size: 13px;
          font-weight: 600;
        }
        .audit-insight-field-value--muted {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-muted);
        }

        .audit-insight-user-stack {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .audit-insight-user-email {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          word-break: break-all;
          line-height: 1.45;
        }
        .audit-insight-user-id {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-muted);
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          word-break: break-all;
          line-height: 1.45;
        }

        .audit-insight-ua-summary {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.45;
        }
        .audit-insight-ua-toggle {
          margin-top: 10px;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 12px;
          font-weight: 700;
          color: #6366F1;
          letter-spacing: 0.02em;
          transition: color 0.2s ease, opacity 0.2s ease;
        }
        .audit-insight-ua-toggle:hover {
          color: #4f46e5;
          opacity: 0.92;
        }
        .audit-insight-ua-full-wrap {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.35s ease, margin-top 0.25s ease;
          margin-top: 0;
        }
        .audit-insight-ua-full-wrap.is-open {
          grid-template-rows: 1fr;
          margin-top: 12px;
        }
        .audit-insight-ua-full-inner {
          overflow: hidden;
          min-height: 0;
        }
        .audit-insight-ua-full {
          margin: 0;
          padding: 12px 14px;
          font-size: 12px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-all;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          color: var(--text-body);
          background: var(--surface-85);
          border: 1px solid var(--border);
          border-radius: 12px;
          max-height: 240px;
          overflow-y: auto;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .audit-insight-ua-full-wrap.is-open .audit-insight-ua-full {
          opacity: 1;
        }

        .audit-insight-desc {
          margin-top: 20px;
          padding: 18px 20px;
          border-radius: 16px;
          border: 1px solid rgba(99, 102, 241, 0.12);
          border-left: 4px solid #6366F1;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.07), rgba(139, 92, 246, 0.04));
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .audit-insight-desc-label {
          display: block;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-muted);
          margin-bottom: 10px;
        }
        .audit-insight-desc-text {
          margin: 0;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.65;
          color: var(--text-body);
        }

        [data-theme="dark"] .audit-insight-outcome-pill--success {
          color: #4ade80;
          background: rgba(34, 197, 94, 0.12);
          border-color: rgba(34, 197, 94, 0.35);
          box-shadow: 0 0 22px rgba(34, 197, 94, 0.15);
        }
        [data-theme="dark"] .audit-insight-outcome-pill--failed {
          color: #fca5a5;
          background: rgba(239, 68, 68, 0.1);
          border-color: rgba(239, 68, 68, 0.35);
          box-shadow: 0 0 22px rgba(239, 68, 68, 0.12);
        }
        [data-theme="dark"] .audit-insight-outcome-pill--flagged {
          color: #facc15;
          background: rgba(234, 179, 8, 0.1);
          border-color: rgba(234, 179, 8, 0.3);
          box-shadow: 0 0 18px rgba(234, 179, 8, 0.12);
        }
        [data-theme="dark"] .audit-insight-panel {
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.35);
        }
        [data-theme="dark"] .audit-insight-panel:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
        }
        [data-theme="dark"] .audit-insight-desc {
          border-color: rgba(129, 140, 248, 0.25);
          border-left-color: #818cf8;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.12), rgba(139, 92, 246, 0.08));
        }
        [data-theme="dark"] .audit-insight-ua-toggle {
          color: #a5b4fc;
        }
        [data-theme="dark"] .audit-insight-ua-toggle:hover {
          color: #c7d2fe;
        }
        [data-theme="dark"] .audit-insight-overlay {
          background-color: rgba(0, 0, 0, 0.72);
        }
      `}</style>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.pageTitle}>Audit Logs</h2>
          <p style={s.subtitle}>
            All user activities (login, logout, uploads, access requests, OTP events) are recorded here. Logs are append-only and immutable.
          </p>
        </div>
        <div style={s.headerActions}>
          {/* Refresh */}
          <button onClick={loadLogs} style={s.refreshBtn} className="hover-icon-btn" title="Refresh logs">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
          {/* Export */}
          <div style={s.exportGroup}>
            <button onClick={exportToPDF} style={s.exportBtn} className="hover-action-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              PDF
            </button>
            <button onClick={exportToExcel} style={s.exportBtn} className="hover-action-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="3" y1="15" x2="21" y2="15"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
              Excel
            </button>
            <button onClick={exportToCSV} style={s.exportBtn} className="hover-action-btn" title="Export as CSV (server-side)">
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Last refresh */}
      <div style={s.refreshNote} title="Browser local time when this view was loaded or refreshed">
        Data fetched: {lastRefresh.toLocaleTimeString('en-IE')} (local time)
      </div>

      <div className="premium-panel audit-log-main-panel">
        <div className="audit-stats-grid" style={s.auditStatsGrid}>
          <div className="premium-stat-card">
            <div className="premium-stat-icon">{iconAuditTotal}</div>
            <div style={s.auditStatValue}>{summaryStats.total.toLocaleString()}</div>
            <div style={s.auditStatLabel}>Total Events</div>
            <span className="premium-stat-trend premium-stat-trend--na">Current view</span>
          </div>
          <div className="premium-stat-card">
            <div className="premium-stat-icon">{iconAuditSuccess}</div>
            <div style={s.auditStatValue}>{summaryStats.success.toLocaleString()}</div>
            <div style={s.auditStatLabel}>Successful Events</div>
            <span className="premium-stat-trend premium-stat-trend--na">—</span>
          </div>
          <div className="premium-stat-card">
            <div className="premium-stat-icon">{iconAuditFailed}</div>
            <div style={s.auditStatValue}>{summaryStats.failed.toLocaleString()}</div>
            <div style={s.auditStatLabel}>Failed Events</div>
            <span className="premium-stat-trend premium-stat-trend--na">—</span>
          </div>
          <div className="premium-stat-card">
            <div className="premium-stat-icon">{iconAuditShield}</div>
            <div style={s.auditStatValue}>{summaryStats.securityAlerts.toLocaleString()}</div>
            <div style={s.auditStatLabel}>Security Alerts</div>
            <span className="premium-stat-trend premium-stat-trend--na">—</span>
          </div>
        </div>

        <form onSubmit={handleFilter} className="audit-filter-row">
          <div style={{ ...s.filterGroup, flex: '1 1 320px', minWidth: 200 }}>
            <label style={s.filterLabel} htmlFor="audit-search-input">Search</label>
            <input
              id="audit-search-input"
              type="text"
              className="audit-filter-input"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
              placeholder="Search logs…"
              style={s.searchInput}
            />
          </div>
          <div style={{ ...s.filterGroup, flex: '0 1 170px', minWidth: 140 }}>
            <label style={s.filterLabel} htmlFor="audit-event-type">Event Type</label>
            <select id="audit-event-type" className="audit-filter-input" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={s.select}>
              {eventTypes.map((type) => (
                <option key={type} value={type}>{type || 'All Events'}</option>
              ))}
            </select>
          </div>
          <div className="audit-filter-date-wrap">
            <div style={{ ...s.filterGroup, flex: '0 1 148px', minWidth: 130 }}>
              <label style={s.filterLabel} htmlFor="audit-start-date">Start Date</label>
              <input id="audit-start-date" type="date" className="audit-filter-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={s.dateInput} />
            </div>
            <div style={{ ...s.filterGroup, flex: '0 1 148px', minWidth: 130 }}>
              <label style={s.filterLabel} htmlFor="audit-end-date">End Date</label>
              <input id="audit-end-date" type="date" className="audit-filter-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={s.dateInput} />
            </div>
          </div>
          <div className="audit-filter-actions" style={{ ...s.filterActions, marginLeft: 'auto' }}>
            <button type="submit" style={s.filterBtn}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Apply
            </button>
            {hasActiveFilters && (
              <button type="button" onClick={clearFilters} style={s.clearBtn}>Clear</button>
            )}
          </div>
        </form>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div style={{ width: 28, height: 28, borderWidth: 3, borderStyle: 'solid', borderColor: 'var(--border)', borderTopColor: 'var(--text-muted)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          </div>
        ) : (
          <>
            <div className="table-responsive audit-table-wrap" style={s.tableContainer}>
              {isMobile ? (
                <div className="audit-mobile-list">
                  {paginatedLogs.map((log) => {
                    const tier = rowOutcomeTier(log.outcome);
                    const pillClass = tier === 'success' ? 'audit-event-pill audit-event-pill--success' : tier === 'failed' ? 'audit-event-pill audit-event-pill--failed' : 'audit-event-pill audit-event-pill--info';
                    const pillLabel = tier === 'success' ? 'Success' : tier === 'failed' ? 'Failed' : 'Info';
                    return (
                      <div
                        key={log.id}
                        className="audit-mobile-card"
                        style={{ borderLeftColor: leftRowIndicatorColor(log.outcome) }}
                        onClick={() => handleView(log.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') handleView(log.id);
                        }}
                      >
                        <div className="audit-mobile-row">
                          <div>
                            <span className={pillClass}>{pillLabel}</span>
                            <div className="audit-mobile-title">{(log.event_type || '').replace(/_/g, ' ')}</div>
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatRelative(log.created_at)}</span>
                        </div>
                        <div className="audit-mobile-desc">{log.description || '—'}</div>
                        <div className="audit-mobile-meta">
                          <span>{formatDate(log.created_at)}</span>
                          <span>{log.user_email || '—'}</span>
                          <span><code>{log.ip_address || '—'}</code>{log.location ? ` · ${log.location}` : ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.thStatic}>Timestamp{tzLabel ? ` (${tzLabel})` : ''}</th>
                      <th style={s.thStatic}>Event</th>
                      <th style={s.thStatic}>Description</th>
                      <th style={s.thStatic}>Resource</th>
                      <th style={s.thStatic}>User</th>
                      <th style={s.thStatic}>IP Address</th>
                      <th style={s.thOutcomeHead}>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLogs.map((log) => {
                      const tier = rowOutcomeTier(log.outcome);
                      const pillClass = tier === 'success' ? 'audit-event-pill audit-event-pill--success' : tier === 'failed' ? 'audit-event-pill audit-event-pill--failed' : 'audit-event-pill audit-event-pill--info';
                      const pillLabel = tier === 'success' ? 'Success' : tier === 'failed' ? 'Failed' : 'Info';
                      return (
                        <tr
                          key={log.id}
                          className="audit-log-row"
                          style={{
                            ...s.tr,
                            cursor: 'pointer',
                            borderLeft: `3px solid ${leftRowIndicatorColor(log.outcome)}`,
                          }}
                          onClick={() => handleView(log.id)}
                        >
                          <td style={s.tdTimestamp}>
                            <div style={s.tdTimestampMain}>{formatDate(log.created_at)}</div>
                            <div style={s.tdTimestampRel}>{formatRelative(log.created_at)}</div>
                          </td>
                          <td style={s.tdEvent}>
                            <div>
                              <span className={pillClass}>{pillLabel}</span>
                              <div style={s.tdEventTypeName}>{(log.event_type || '').replace(/_/g, ' ')}</div>
                            </div>
                          </td>
                          <td style={s.tdDesc} title={log.description || undefined}>{log.description}</td>
                          <td style={s.td}>
                            <code style={s.resourceCode}>{log.resource || '—'}</code>
                          </td>
                          <td style={s.tdUser}>
                            <div style={s.tdUserMain}>{log.user_email || '—'}</div>
                            <div style={s.tdUserSub}>{log.user_id !== 'unknown' ? log.user_id : '—'}</div>
                          </td>
                          <td style={s.tdIp}>
                            <div style={s.tdIpMain}>{log.ip_address}</div>
                            {log.location && <div style={s.tdIpSub}>{log.location}</div>}
                          </td>
                          <td style={s.tdOutcome}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: outcomeCellColor(log.outcome) }}>
                              {log.outcome || '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {filteredLogs.length === 0 && (
                <div style={s.emptyState}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <p style={{ margin: '10px 0 0', color: 'var(--text-muted)', fontSize: 16 }}>
                    {hasActiveFilters
                      ? 'No audit logs found matching the filters.'
                      : 'No audit logs yet. User actions (login, upload, OTP) will appear here in real-time.'}
                  </p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div style={s.pagination}>
                <span style={s.pageInfo}>
                  Showing {((safePage - 1) * effectivePageSize) + 1}–{Math.min(safePage * effectivePageSize, sortedLogs.length)} of {sortedLogs.length}
                </span>
                <div style={s.pageButtons}>
                  <button type="button" className="audit-page-btn" disabled={safePage === 1} onClick={() => setCurrentPage(1)}>
                    &#171;
                  </button>
                  <button type="button" className="audit-page-btn" disabled={safePage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                    &#8249;
                  </button>
                  {Array.from({ length: Math.min(maxVisiblePageButtons, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= maxVisiblePageButtons) {
                      page = i + 1;
                    } else if (currentPage <= Math.ceil(maxVisiblePageButtons / 2)) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - Math.floor(maxVisiblePageButtons / 2)) {
                      page = totalPages - (maxVisiblePageButtons - 1) + i;
                    } else {
                      page = currentPage - Math.floor(maxVisiblePageButtons / 2) + i;
                    }
                    return (
                      <button
                        type="button"
                        key={page}
                        className={`audit-page-btn${page === safePage ? ' audit-page-btn--active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button type="button" className="audit-page-btn" disabled={safePage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                    &#8250;
                  </button>
                  <button type="button" className="audit-page-btn" disabled={safePage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    &#187;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Audit detail modal — resolved by ID only; UI insight layout (no backend change) */}
      {selectedLog && (
        <AuditLogInsightModal
          log={selectedLog}
          formatDate={formatDate}
          onClose={() => setSelectedLogId(null)}
          uaDetailExpanded={uaDetailExpanded}
          setUaDetailExpanded={setUaDetailExpanded}
          overlayStyle={s.modalOverlay}
          sheetStyle={s.modal}
        />
      )}

      {/* Immutability Note */}
      <div style={s.note}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <span>
          <strong>Append-only log:</strong> Audit entries are immutable once written.
          They cannot be edited or deleted by any user, including administrators.
        </span>
      </div>
    </div>
  );
}

function AuditLogInsightModal({
  log,
  formatDate,
  onClose,
  uaDetailExpanded,
  setUaDetailExpanded,
  overlayStyle,
  sheetStyle,
}) {
  const uaShort = parseUserAgentShort(log.user_agent);
  const outcomeText = log.outcome || '—';
  const outcomeClass = `audit-insight-outcome-pill audit-insight-outcome-pill--${outcomePillVariant(log.outcome)}`;
  const eventTitle = (log.event_type || '').replace(/_/g, ' ');
  const uaFullId = `audit-insight-ua-full-${log.id}`;

  return (
    <div
      className="audit-insight-overlay"
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="audit-insight-sheet" style={sheetStyle} role="dialog" aria-modal="true" aria-labelledby="audit-insight-dialog-title">
        <div className="audit-insight-topbar">
          <span className="audit-insight-eyebrow">Security event detail</span>
          <button type="button" onClick={onClose} className="audit-insight-close" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="audit-insight-hero">
          <div>
            <h2 id="audit-insight-dialog-title" className="audit-insight-title">{eventTitle}</h2>
            <p className="audit-insight-subtitle">{getEventCategorySubtitle(log.event_type)}</p>
          </div>
          <span className={outcomeClass}>{outcomeText}</span>
        </div>

        <div className="audit-insight-body">
          <section className="audit-insight-panel" aria-label="Event information">
            <h3 className="audit-insight-section-title">Event info</h3>
            <div className="audit-insight-grid-2">
              <DetailItem label="Timestamp" value={formatDate(log.created_at)} />
              <DetailItem label="Event Type" value={log.event_type} />
              <DetailItem label="Outcome" value={log.outcome || '—'} />
              <DetailItem label="Log ID" value={log.id} mono />
            </div>
          </section>

          <section className="audit-insight-panel" aria-label="User information">
            <h3 className="audit-insight-section-title">User info</h3>
            <div className="audit-insight-user-stack">
              <div>
                <span className="audit-insight-field-label">User Email</span>
                <div className="audit-insight-user-email">{log.user_email || '—'}</div>
              </div>
              <div>
                <span className="audit-insight-field-label">User ID</span>
                <div className="audit-insight-user-id">{log.user_id !== 'unknown' ? log.user_id : '—'}</div>
              </div>
            </div>
          </section>

          <section className="audit-insight-panel" aria-label="Technical information">
            <h3 className="audit-insight-section-title">Technical info</h3>
            <div className="audit-insight-grid-2">
              <DetailItem label="IP Address" value={log.ip_address || '—'} mono />
              <DetailItem label="Location" value={log.location || '—'} />
              <DetailItem label="Resource" value={log.resource || '—'} />
              {log.duration != null && (
                <DetailItem label="Duration" value={`${log.duration}ms`} />
              )}
              {log.session_id && (
                <DetailItem label="Session ID" value={log.session_id} mono />
              )}
            </div>
          </section>

          <section className="audit-insight-panel" aria-label="Request details">
            <h3 className="audit-insight-section-title">Request details</h3>
            <div>
              <span className="audit-insight-field-label">User Agent</span>
              {log.user_agent ? (
                <>
                  <div className="audit-insight-ua-summary">{uaShort.label}</div>
                  <button
                    type="button"
                    className="audit-insight-ua-toggle"
                    onClick={() => setUaDetailExpanded((v) => !v)}
                    aria-expanded={uaDetailExpanded}
                    aria-controls={uaFullId}
                  >
                    {uaDetailExpanded ? 'Hide full details' : 'Show full details'}
                  </button>
                  <div className={`audit-insight-ua-full-wrap${uaDetailExpanded ? ' is-open' : ''}`}>
                    <div className="audit-insight-ua-full-inner">
                      <pre id={uaFullId} className="audit-insight-ua-full">{uaShort.full}</pre>
                    </div>
                  </div>
                </>
              ) : (
                <span className="audit-insight-field-value">—</span>
              )}
            </div>
          </section>
        </div>

        <div className="audit-insight-desc">
          <span className="audit-insight-desc-label">Full description</span>
          <p className="audit-insight-desc-text">{log.description}</p>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, mono }) {
  return (
    <div>
      <span className="audit-insight-field-label">{label}</span>
      <span className={`audit-insight-field-value${mono ? ' audit-insight-field-value--mono' : ''}`}>{value}</span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = {
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px 0', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, fontWeight: 500 },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 28, gap: 16, flexWrap: 'wrap',
  },
  headerActions: {
    display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
  },
  refreshBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: 8,
    border: '1px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    cursor: 'pointer', color: 'var(--text-secondary)', transition: 'all 0.25s ease',
    boxShadow: '0 1px 4px rgba(99,102,241,0.04)',
  },
  exportGroup: { display: 'flex', gap: 8 },
  exportBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: 8,
    border: '1px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    color: 'var(--text-body)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxShadow: '0 1px 4px rgba(99,102,241,0.04)',
  },

  refreshNote: {
    fontSize: 13, color: 'var(--text-muted)', marginBottom: 24, fontWeight: 500,
  },

  auditStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 20,
    marginBottom: 28,
  },
  auditStatValue: {
    fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', lineHeight: 1.08,
  },
  auditStatLabel: {
    fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginTop: 6,
  },

  filterGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  filterLabel: { fontSize: 10, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  searchInput: {
    padding: '9px 14px', border: '1px solid var(--border)', borderRadius: 10,
    fontSize: 13, backgroundColor: 'var(--surface-90)', color: 'var(--text-primary)',
    height: 38, boxSizing: 'border-box', width: '100%', minWidth: 0,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  },
  select: {
    padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 10,
    fontSize: 13, backgroundColor: 'var(--surface-90)', color: 'var(--text-primary)',
    height: 38, boxSizing: 'border-box', width: '100%',
  },
  dateInput: {
    padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 10,
    fontSize: 13, color: 'var(--text-primary)', backgroundColor: 'var(--surface-90)',
    height: 38, boxSizing: 'border-box', width: '100%',
  },
  filterActions: { display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 },
  filterBtn: {
    padding: '9px 18px',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    color: '#fff',
    border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6,
    height: 38, transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: '0 2px 10px rgba(99,102,241,0.28)',
  },
  clearBtn: {
    padding: '9px 16px', backgroundColor: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 10,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, height: 38,
    transition: 'all 0.2s ease',
  },

  tableContainer: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    border: 'none',
    overflow: 'auto',
    marginBottom: 16,
    boxShadow: 'none',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 },
  th: {
    textAlign: 'left', padding: '12px 14px',
    background: 'transparent',
    fontWeight: 800, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em',
    cursor: 'pointer', userSelect: 'none',
  },
  thStatic: {
    textAlign: 'left', padding: '12px 14px',
    background: 'transparent',
    fontWeight: 800, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em',
    cursor: 'default',
  },
  thOutcomeHead: {
    textAlign: 'right', padding: '12px 14px',
    background: 'transparent',
    fontWeight: 800, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em',
    cursor: 'default',
  },
  tr: { transition: 'background-color 0.15s ease' },
  td: {
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    color: 'var(--text-body)', fontSize: 12, lineHeight: 1.45, verticalAlign: 'top',
  },
  tdTimestamp: {
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    verticalAlign: 'top', minWidth: 140,
  },
  tdTimestampMain: { color: 'var(--text-primary)', fontSize: 12, whiteSpace: 'nowrap', fontWeight: 600 },
  tdTimestampRel: { color: 'var(--text-muted)', fontSize: 10, marginTop: 2, whiteSpace: 'nowrap' },
  tdEvent: {
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    verticalAlign: 'top', minWidth: 118,
  },
  tdEventTypeName: {
    fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1.35,
    letterSpacing: '0.02em',
  },
  tdDesc: {
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    color: 'var(--text-body)', fontSize: 14, fontWeight: 500, lineHeight: 1.45,
    maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  resourceCode: {
    fontSize: 11, color: 'var(--text-secondary)',
    background: 'var(--surface-85)',
    padding: '3px 8px', borderRadius: 6, fontFamily: 'monospace', display: 'inline-block',
    border: '1px solid var(--border)',
  },
  tdUser: { padding: '12px 14px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', minWidth: 150 },
  tdUserMain: { color: 'var(--text-secondary)', fontSize: 11, wordBreak: 'break-all', fontWeight: 500 },
  tdUserSub: { color: 'var(--text-muted)', fontSize: 11, fontFamily: 'ui-monospace, monospace', marginTop: 3, wordBreak: 'break-all' },
  tdIp: { padding: '12px 14px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', minWidth: 110 },
  tdIpMain: { fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' },
  tdIpSub: { fontSize: 11, color: 'var(--text-muted)', marginTop: 3 },
  tdOutcome: {
    padding: '12px 14px', borderBottom: '1px solid var(--border)',
    verticalAlign: 'middle', whiteSpace: 'nowrap', textAlign: 'right',
  },
  emptyState: { textAlign: 'center', padding: '36px 24px' },

  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,16,40,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24,
    backdropFilter: 'blur(6px)',
  },
  modal: {
    backgroundColor: 'var(--surface-95)', borderRadius: 24,
    maxWidth: 700, width: '100%', maxHeight: '85vh', overflowY: 'auto',
    padding: 28, boxShadow: '0 24px 80px rgba(99,102,241,0.18), 0 8px 24px rgba(0,0,0,0.08)',
    border: '1.5px solid rgba(99,102,241,0.1)',
    backdropFilter: 'blur(20px)',
  },

  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 8,
    marginBottom: 0,
    flexWrap: 'wrap', gap: 10,
  },
  pageInfo: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
  pageButtons: { display: 'flex', gap: 8, alignItems: 'center' },

  note: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.03))',
    padding: '10px 14px',
    borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)',
    lineHeight: 1.4, border: '1px solid rgba(99,102,241,0.1)',
  },
};

export default AuditLogsPage;
